import React, { Suspense } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PromotionUpdateDialog_promotion$key } from '../../../relay/__generated__/PromotionUpdateDialog_promotion.graphql';
import { PromotionUpdateDialog_promotionUpdateMutation } from '../../../relay/__generated__/PromotionUpdateDialog_promotionUpdateMutation.graphql';
import Button from '../../core/Button';
import DatetimeField from '../../core/DatetimeField';
import Dialog, { DialogProps } from '../../core/Dialog';
import EnumPairSelectField from '../../core/EnumPairSelectField';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import SQLEditorField from '../../core/SQLEditorField';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import View from '../../core/View';

const PromotionUpdateDialog_promotion = graphql`
  fragment PromotionUpdateDialog_promotion on Promotion {
    id
    title
    priority
    startAt
    endAt
    isRepeat
    repeatType
    repeatValue
    query
  }
`;

type Props = {
  promotion: PromotionUpdateDialog_promotion$key;
} & DialogProps &
  Pick<MutationFormikProps<PromotionUpdateDialog_promotionUpdateMutation>, 'connections' | 'config' | 'onSubmit'>;

const PromotionUpdateDialog = ({ promotion, connections, config, onSubmit, ...props }: Props) => {
  const _promotion = useFragment(PromotionUpdateDialog_promotion, promotion);
  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontWeight: 'bold', fontSize: 3 }}>프로모션 수정하기</Text>
      </Dialog.Header>
      <MutationFormik<PromotionUpdateDialog_promotionUpdateMutation>
        mutation={graphql`
          mutation PromotionUpdateDialog_promotionUpdateMutation($input: PromotionUpdateInput!) {
            promotionUpdate(input: $input) {
              id
              title
              priority
              startAt
              endAt
              isRepeat
              repeatType
              repeatValue
              nextOccurrences(limit: 1)
              query
            }
          }
        `}
        initialValues={{
          ..._promotion,
        }}
        connections={connections}
        config={config}
        onSubmit={onSubmit}
        valuesTransformer={(values) => {
          return {
            ...values,
            repeatValue: values?.repeatValue ? values?.repeatValue : null,
            repeatType: values?.repeatType ? values?.repeatType : null,
          };
        }}
      >
        {({ values, setFieldValue, handleSubmit }, { nonFieldErrorRef, scrollContainerRef }) => (
          <>
            <Dialog.Body ref={scrollContainerRef}>
              <NonFieldError ref={nonFieldErrorRef} sx={{ marginBottom: 3 }} />
              <FormLayout>
                <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>프로모션 정보</Text>
                <TextField
                  label={'프로모션명'}
                  name={'title'}
                  caption={'서비스에서는 노출되지 않습니다'}
                  placeholder={'프로모션명 입력'}
                  required
                />
                <NumberField
                  label={'우선순위'}
                  required
                  inputMode={'text'}
                  placeholder={'우선순위 입력'}
                  caption={'낮은 숫자일수록 우선적으로 노출됩니다'}
                  name={'priority'}
                />
                <View>
                  <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>진행 기간 *</Text>
                  <View sx={{ mt: '12px', p: '12px', backgroundColor: 'canvas.subtle', borderRadius: 2 }}>
                    <FormLayout>
                      <DatetimeField
                        label={'시작일'}
                        name={'startAt'}
                        datePickerProps={{ placeholder: '시작일 선택' }}
                        required
                      />
                      <DatetimeField
                        label={'종료일'}
                        name={'endAt'}
                        datePickerProps={{ placeholder: '종료일 선택' }}
                        required
                      />
                      <Grid gapX={2}>
                        <Grid.Unit size={1 / 2}>
                          <EnumPairSelectField
                            typename={'RepeatTypeEnum'}
                            name={'repeatType'}
                            label={'주기'}
                            required
                            placeholder={'반복 없음'}
                            selectablePlaceholder
                            onChange={(value) => {
                              setFieldValue('isRepeat', !!value);
                              setFieldValue('repeatType', value ? value : undefined);
                              setFieldValue('repeatValue', undefined);
                            }}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={1 / 2}>
                          <Suspense fallback={<Spinner />}>
                            <EnumPairSelectField
                              typename={values.repeatType === 'monthly' ? 'MonthDayEnum' : 'WeekdayEnum'}
                              name={'repeatValue'}
                              label={'주기 값'}
                              placeholder={'-'}
                              disabled={!values.repeatType || values.repeatType === 'daily'}
                              labelConfig={{ sx: { visibility: 'hidden' } }}
                              validate={(value) => {
                                if (!value && (values.repeatType === 'monthly' || values.repeatType === 'weekly')) {
                                  return `‘-’ 이외의 옵션을 선택해주세요'`;
                                }
                              }}
                            />
                          </Suspense>
                        </Grid.Unit>
                      </Grid>
                    </FormLayout>
                  </View>
                </View>
              </FormLayout>
              <View sx={{ mt: 7 }}>
                <FormLayout>
                  <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>유저 정보</Text>
                  <SQLEditorField label={'SQL'} name={'query'} height={200} required />
                </FormLayout>
              </View>
            </Dialog.Body>
            <Dialog.Footer>
              <Button size={'large'} variant={'primary'} onClick={() => handleSubmit()}>
                수정하기
              </Button>
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default PromotionUpdateDialog;
